import React, { useState, useEffect, useCallback, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import Cookies from "universal-cookie";
import config from "../../config.js";
import swal from "sweetalert";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import mixpanel from "mixpanel-browser";
import Table from "../Table.js";
import { log, error } from "../../utils/logger.js";
const RoleTable = ({
  setRoleEditData,
  setSelectedRole,
  selectedRole,
  deleteButton,
  editButton,
  defaultOnly,
  actionInitiatingUser,
  setCanUserEdit,
}) => {
  const [roles, setRoles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState("");

  const cookies = new Cookies();
  const headers = {
    Authorization: `Bearer ${cookies.get("userToken")}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    const fetchData = async () => {
      const userId = cookies.get("userID");
      const urlStr = defaultOnly
        ? "/permissions/without-default/findall"
        : "/permissions/findall";
      try {
        const res = await axios.post(
          `${config.BACKEND_URL}${urlStr}`,
          { userId },
          { headers }
        );
        setRoles(res.data);
      } catch (err) {
        log(err);
      }
    };
    fetchData();
  }, [defaultOnly]);

  useEffect(() => {
    log("Role table rendered");
  });

  const handleClose = () => setDialogOpen(false);

  const decodePermissions = (binary, letters) =>
    binary
      .split("")
      .map((bit, idx) => (bit === "1" ? letters[idx] : ""))
      .join("");

  const handleClick = useCallback(
    (e, cell, data) => {
      // const data = cell.getData(); // ✅ Get row data from the cell
      setDialogOpen(true);

      const permissions = {
        alarm: decodePermissions(data.alarm, "VCED"),
        dashboard: decodePermissions(data.dashboard, "VCED"),
        report: data.report[0] === "1" ? "V" : "",
        role: decodePermissions(data.role, "VCEDA"),
        source: decodePermissions(data.source, "VCED"),
        user: decodePermissions(data.user, "VCED"),
        userDashboard: decodePermissions(data.userDashboard, "VCED"),
        userGroup: decodePermissions(data.userGroup, "VCED"),
        view: decodePermissions(data.view, "VCED"),
      };

      const message = Object.entries(permissions)
        .map(
          ([key, value]) =>
            `${key.charAt(0).toUpperCase() + key.slice(1)}: ${
              value || "No Role"
            }`
        )
        .join("\n");

      setDialogContent(`Name: ${data.name}\n${message}`);
    },
    [setDialogOpen, setDialogContent]
  );

  const memoizedColumns = useMemo(
    () => [
      { title: "Rolename", field: "name", headerFilter: "input", width: "40%" },
      {
        title: "Information",
        width: "40%",
        formatter: () =>
          '<button class="btn btn-outline-primary btn-sm">Details</button>',
        cellClick: (e, cell) => {
          e.stopPropagation(); // prevent row selection
          // Call your handleClick function with row data
          handleClick(e, cell, cell.getRow().getData());
          cell.getTable().deselectRow();
        },
      },
    ],
    [handleClick]
  ); // Include handleClick if it's a stable function

  const handleEdit = async () => {
    mixpanel.track("Edit Role Page Edit Button Clicked", {
      userID: cookies.get("userID"),
    });
    const selectedData = roles.filter((role) => selectedRole.includes(role.id));
    if (selectedData.length !== 1) {
      swal(
        "Only one role can be edited at a time",
        "Please try again",
        "error"
      );
      return;
    }
    try {
      const res = await axios.get(
        `${config.BACKEND_URL}/permissions/getById/${selectedData[0].id}`,
        { headers }
      );
      if (res.status === 200) {
        setRoleEditData(res.data);
        setCanUserEdit(true);
      }
    } catch (err) {
      error(err);
    }
  };

  const handleDelete = async () => {
    mixpanel.track("Delete Role Page Delete Button Clicked", {
      userID: cookies.get("userID"),
    });
    const selectedData = roles.filter((role) => selectedRole.includes(role.id));
    if (!selectedData.length) return;
    const confirmed = await swal(
      "Are you sure you want to delete selected roles?",
      {
        buttons: { cancel: "Cancel", delete: "Delete" },
        icon: "warning",
      }
    );
    if (confirmed === "delete") {
      try {
        const res = await axios.post(
          `${config.BACKEND_URL}/permissions/delete`,
          { selectedData: selectedData.map((r) => r.id), actionInitiatingUser },
          { headers }
        );
        if (res.status === 204) {
          swal("Roles Deleted", "Click OK to continue...", "success").then(() =>
            window.location.reload()
          );
        }
      } catch (err) {
        error(err);
      }
    }
  };

  const additionalControls = (
    <>
      {deleteButton && (
        <button className="btn btn-danger" onClick={handleDelete}>
          Delete
        </button>
      )}
      {editButton && (
        <button className="btn btn-primary ms-2" onClick={handleEdit}>
          Edit
        </button>
      )}
    </>
  );
  const handleSelectionChange = useCallback(
    (selected) => {
      const ids = selected.map((role) => role.id);
      setSelectedRole(ids);
      log("Selected roles:", ids);
    },
    [] // Add dependencies here if needed (e.g., if setSelectedRole changes)
  );

  const memoizedData = useMemo(() => roles, [roles]);

  return (
    <>
      <div className="RoleTable" style={{ padding: "25px 0" }}>
        <Table
          tableID="roleTable"
          tableData={memoizedData}
          tableColumns={memoizedColumns}
          displaySerial
          onSelectionChange={handleSelectionChange}
        />
        {additionalControls}
      </div>
      {/* <Dialog open={dialogOpen} onClose={handleClose} disableScrollLock>
        <DialogContent>
          <div style={{ whiteSpace: "pre-line", fontSize: "1.2rem" }}>
            {dialogContent.split("\n").map((line, i) => {
              const [key, value] = line.split(":");
              return (
                <div key={i}>
                  <strong>{key}:</strong> {value}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog> */}

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
            bgcolor: "#0f172a",
            color: "white",
          }}
        >
          Information
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        {/* Content */}
        <DialogContent sx={{ bgcolor: "#f8fafc", py: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {dialogContent.split("\n").map((line, i) => {
              const [key, value] = line.split(":");
              return (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    gap: 1,
                    fontSize: "0.95rem",
                    bgcolor: "white",
                    p: 1.5,
                    borderRadius: 1.5,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, minWidth: 120 }}>
                    {key}:
                  </Typography>
                  <Typography sx={{ color: "#475569" }}>
                    {value || "-"}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RoleTable;
